"use strict";
((UP) => {
    ((Updater) => {
        const NULL = null;

        // sessionStorage keys (survive an in-tab reload, cleared when the tab closes)
        const successKey = 'pwa-update-succeeded';      // version we navigated to apply
        const reloadCountKey = 'pwa-auto-reload-count';  // backstop against reload loops

        // Timers / limits
        const stallTimeoutMs = 8000;        // reveal "Reload now" if activation stalls after SKIP_WAITING
        const hardReloadTimeoutMs = 25000;  // force navigation if activation was requested but no signal fired
        const installWaitTimeoutMs = 30000;
        const updateCheckDebounceMs = 5000;
        // A genuinely broken deploy (manifest newer than the served assets, or an install that keeps
        // aborting on integrity) must not reload forever. After this many auto-reloads in one tab we
        // stop auto-updating and let the user drive it.
        const maxAutoReloadsPerSession = 2;

        // Status values mirrored to .NET so the banner can reflect, never decide.
        const Status = {Idle: 'idle', Updating: 'updating', Stalled: 'stalled', Reloading: 'reloading'};

        const getAttribute = (name) => document.currentScript?.getAttribute(name);
        const serviceWorkerScriptPath = getAttribute("register") || "service-worker.js";
        const noRegister = getAttribute("no-register");
        const detectBotPattern = getAttribute("detect-bot-pattern") || "google|baidu|bingbot|duckduckbot|teoma|slurp|yandex";

        // State
        let initialInstallation = false;
        let reloading = false;
        let activationRequested = false; // we have asked a worker to take over via SKIP_WAITING
        let currentRegistration = NULL;
        let activationWorker = NULL;     // the exact worker we are activating
        let activationListener = NULL;   // its statechange listener, tracked so it can be removed
        let pendingVersion = NULL;       // version we are moving to (for the success flag + toast)
        let lastUpdateCheckAt = 0;
        let autoUpdateInFlight = NULL;
        let autoUpdateCancelled = false;
        let status = Status.Idle;
        let stallTimer = NULL;
        let hardReloadTimer = NULL;

        const monitoredWorkers = new WeakSet();

        let resolveDotNetReady;
        const waitForDotNetReady = new Promise((resolve) => {
            resolveDotNetReady = resolve;
        });
        let dotNetRef = NULL;

        // sessionStorage access is wrapped because it throws in some privacy modes / sandboxed frames.
        const readSession = (key) => {
            try {
                return sessionStorage.getItem(key);
            } catch {
                return NULL;
            }
        };
        const writeSession = (key, value) => {
            try {
                sessionStorage.setItem(key, value);
            } catch {
            }
        };
        const removeSession = (key) => {
            try {
                sessionStorage.removeItem(key);
            } catch {
            }
        };

        const getAutoReloadCount = () => {
            const parsed = Number(readSession(reloadCountKey));
            return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
        };

        // --- status: pushed to .NET and queryable, so a detection that lands before the
        // component is ready is never lost. ---
        const dotNet = () => dotNetRef !== NULL ? Promise.resolve(dotNetRef) : waitForDotNetReady;

        const notifyStatus = (statusValue, version) => {
            dotNet().then(ref => ref.invokeMethodAsync('OnUpdaterStatusChanged', statusValue, version ?? NULL)).catch(() => {
            });
        };

        const setStatus = (newStatus, version) => {
            const newVersion = version !== undefined && version !== NULL ? version : pendingVersion;
            if (status === newStatus && pendingVersion === newVersion) {
                return;
            }

            status = newStatus;
            pendingVersion = newVersion;
            console.log(`[PWA Updater] status -> ${status}${pendingVersion ? ` (${pendingVersion})` : ''}`);
            notifyStatus(status, pendingVersion);
        };

        Updater.getStatus = () => ({status, version: pendingVersion});

        const clearStallTimer = () => {
            if (stallTimer !== NULL) {
                clearTimeout(stallTimer);
                stallTimer = NULL;
            }
        };

        const showStalledIfActivationStalls = (version) => {
            clearStallTimer();
            stallTimer = setTimeout(() => {
                if (!reloading && activationRequested && status === Status.Updating) {
                    setStatus(Status.Stalled, version);
                }
            }, stallTimeoutMs);
        };

        const stopIfAutoUpdateCancelled = () => {
            if (!autoUpdateCancelled) {
                return false;
            }

            clearStallTimer();
            return true;
        };

        // Tear down everything watching the in-progress activation: the worker's statechange
        // listener and timers. Keeping the listener tracked here prevents a superseded
        // activation's listener from lingering and firing an unexpected reload later.
        const stopActivationWatch = () => {
            if (activationWorker !== NULL && activationListener !== NULL) {
                activationWorker.removeEventListener('statechange', activationListener);
            }
            activationWorker = NULL;
            activationListener = NULL;

            clearStallTimer();
            if (hardReloadTimer !== NULL) {
                clearTimeout(hardReloadTimer);
                hardReloadTimer = NULL;
            }
        };

        // The ONLY path that records the applied version and navigates. Every activation signal
        // (worker state, controllerchange, hard fallback, manual) funnels through here, guarded so
        // it runs exactly once.
        const triggerReload = (reason, version) => {
            if (reloading) {
                return;
            }

            reloading = true;
            stopActivationWatch();

            const applied = version ?? pendingVersion;
            if (applied) {
                writeSession(successKey, applied);
            }
            writeSession(reloadCountKey, String(getAutoReloadCount() + 1));

            setStatus(Status.Reloading, applied);
            console.log(`[PWA Updater] reloading (${reason})`);
            setTimeout(() => window.location.reload(), 10);
        };

        // Notify .NET that a new worker is installed/waiting. .NET owns the decision to act on it.
        // Always pass null: JS does not authoritatively know the deployed version, and pendingVersion
        // can linger (e.g. after a dismissed update) and be stale here. .NET fetches the fresh version.
        const notifyUpdateDetected = () => {
            dotNet().then(ref => ref.invokeMethodAsync('OnUpdateDetected', NULL)).catch(() => {
            });
        };

        const handleWorkerState = (worker, suppress) => {
            if (worker.state === 'installed' && !initialInstallation && !suppress) {
                notifyUpdateDetected();
            }

            if (worker.state === 'activated') {
                initialInstallation = false;
            }
        };

        const monitorWorker = (worker, suppress = false) => {
            if (worker === NULL) {
                return;
            }

            if (!monitoredWorkers.has(worker)) {
                monitoredWorkers.add(worker);
                // Propagate the captured suppression to later transitions too. Hardcoding false
                // here previously leaked an "update available" notification mid-way through a
                // silent activation, flashing the banner just before the auto-reload.
                worker.addEventListener('statechange', () => handleWorkerState(worker, suppress));
            }

            handleWorkerState(worker, suppress);
        };

        const syncRegistrationState = (registration, suppress) => {
            if (registration === NULL) {
                return;
            }

            if (registration.installing) {
                monitorWorker(registration.installing, suppress);
            }
            if (registration.waiting) {
                monitorWorker(registration.waiting, suppress);
            }
            if (registration.active) {
                monitorWorker(registration.active, true);
            }
        };

        const getLatestRegistration = async () => {
            if (currentRegistration === NULL || !navigator.serviceWorker?.getRegistration) {
                return currentRegistration;
            }

            try {
                const latest = await navigator.serviceWorker.getRegistration(currentRegistration.scope);
                if (latest) {
                    currentRegistration = latest;
                }
            } catch (err) {
                console.warn('[PWA Updater] Failed to refresh registration:', err);
            }

            return currentRegistration;
        };

        const isInstalledOrLater = (worker) =>
            worker.state === 'installed' || worker.state === 'activating' || worker.state === 'activated';

        const waitForWorkerInstalled = (worker, timeoutMs) => new Promise(resolve => {
            if (worker.state === 'redundant') {
                resolve(false);
                return;
            }
            if (isInstalledOrLater(worker)) {
                resolve(true);
                return;
            }

            let settled = false;
            const settle = (success) => {
                if (settled) {
                    return;
                }
                settled = true;
                worker.removeEventListener('statechange', listener);
                clearTimeout(timer);
                resolve(success);
            };

            const listener = () => {
                if (worker.state === 'redundant') {
                    settle(false);
                } else if (isInstalledOrLater(worker)) {
                    settle(true);
                }
            };

            worker.addEventListener('statechange', listener);
            const timer = setTimeout(() => settle(false), timeoutMs);
        });

        const waitForOperation = (operation, timeoutMs) => new Promise(resolve => {
            let settled = false;
            const settle = (result) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                resolve(result);
            };

            const timer = setTimeout(() => settle({status: 'timeout'}), timeoutMs);
            operation
                .then(value => settle({status: 'resolved', value}))
                .catch(error => settle({status: 'rejected', error}));
        });

        // Arm every reload signal against the exact worker we are activating, then ask it to take over.
        const armActivation = (worker, version) => {
            stopActivationWatch();
            activationWorker = worker;
            activationRequested = true;
            setStatus(Status.Updating, version);

            // Listen on the precise worker (not a re-fetched registration.waiting, which goes null
            // the instant the worker leaves the waiting state — the gap that left some browsers,
            // notably Edge, activated but never reloaded).
            activationListener = () => {
                if (worker.state === 'activated') {
                    triggerReload('worker-activated', version);
                }
            };
            worker.addEventListener('statechange', activationListener);

            if (worker.state === 'activated') {
                triggerReload('worker-activated', version);
                return;
            }

            try {
                worker.postMessage({type: 'SKIP_WAITING'});
            } catch (err) {
                console.warn('[PWA Updater] SKIP_WAITING postMessage failed:', err);
            }

            showStalledIfActivationStalls(version);

            // Long timer: we asked for activation and nothing navigated. Force it, unless we have
            // already looped — then leave it to the user so a broken deploy can't reload endlessly.
            hardReloadTimer = setTimeout(() => {
                if (reloading || !activationRequested) {
                    return;
                }
                if (getAutoReloadCount() >= maxAutoReloadsPerSession) {
                    setStatus(Status.Stalled, version);
                    return;
                }
                triggerReload('hard-timeout', version);
            }, hardReloadTimeoutMs);
        };

        // Public API ----------------------------------------------------------------

        Updater.setToBeReady = (ref) => {
            dotNetRef = ref;
            resolveDotNetReady(ref);
        };

        // The single entry point used by .NET for BOTH the stale-on-open case and later in-session
        // version changes. .NET has already decided we should update (environment gate); JS just
        // executes the mechanics and reloads.
        Updater.autoUpdateToLatest = async (version) => {
            if (autoUpdateInFlight !== NULL) {
                return autoUpdateInFlight;
            }

            autoUpdateInFlight = (async () => {
                try {
                    // Once activation has been requested there is nothing more to start; a later poll
                    // tick must not arm a second activation while the first is still settling.
                    if (reloading || activationRequested) {
                        return;
                    }
                    autoUpdateCancelled = false;
                    if (version) {
                        pendingVersion = version;
                    }

                    if (getAutoReloadCount() >= maxAutoReloadsPerSession) {
                        console.warn('[PWA Updater] auto-reload cap reached; awaiting manual reload');
                        setStatus(Status.Stalled, version);
                        return;
                    }

                    // Surface the banner immediately — on a large app the install below can take a
                    // while, and the user should see the update is underway, not a blank wait.
                    setStatus(Status.Updating, version);
                    let registration = await getLatestRegistration();
                    if (stopIfAutoUpdateCancelled()) {
                        return;
                    }
                    if (registration === NULL) {
                        console.warn('[PWA Updater] autoUpdateToLatest: no registration available');
                        clearStallTimer();
                        setStatus(Status.Stalled, version);
                        return;
                    }

                    syncRegistrationState(registration, true);

                    if (registration.waiting) {
                        armActivation(registration.waiting, version);
                        return;
                    }

                    let updateOperation;
                    try {
                        updateOperation = registration.update();
                    } catch (err) {
                        console.warn('[PWA Updater] autoUpdateToLatest: update() failed', err);
                        clearStallTimer();
                        setStatus(Status.Stalled, version);
                        return;
                    }

                    const updateResult = await waitForOperation(updateOperation, installWaitTimeoutMs);
                    if (stopIfAutoUpdateCancelled()) {
                        return;
                    }
                    if (updateResult.status === 'rejected') {
                        console.warn('[PWA Updater] autoUpdateToLatest: update() failed', updateResult.error);
                        clearStallTimer();
                        setStatus(Status.Stalled, version);
                        return;
                    }
                    const updateTimedOut = updateResult.status === 'timeout';
                    if (updateTimedOut) {
                        console.warn('[PWA Updater] autoUpdateToLatest: update() timed out');
                    }

                    registration = await getLatestRegistration() ?? registration;
                    if (stopIfAutoUpdateCancelled()) {
                        return;
                    }
                    syncRegistrationState(registration, true);

                    if (registration.waiting) {
                        armActivation(registration.waiting, version);
                        return;
                    }

                    if (registration.installing) {
                        const reached = await waitForWorkerInstalled(registration.installing, installWaitTimeoutMs);
                        if (stopIfAutoUpdateCancelled()) {
                            return;
                        }
                        registration = await getLatestRegistration() ?? registration;
                        if (stopIfAutoUpdateCancelled()) {
                            return;
                        }
                        if (reached && registration.waiting) {
                            armActivation(registration.waiting, version);
                            return;
                        }

                        // Installed but never reached "waiting", or timed out — offer the manual fallback.
                        console.warn('[PWA Updater] autoUpdateToLatest: install produced no activatable worker');
                        clearStallTimer();
                        setStatus(Status.Stalled, version);
                        return;
                    }

                    if (updateTimedOut) {
                        clearStallTimer();
                        setStatus(Status.Stalled, version);
                        return;
                    }

                    // update() surfaced nothing to activate — the running build is already current.
                    console.log('[PWA Updater] autoUpdateToLatest: already current');
                    clearStallTimer();
                    setStatus(Status.Idle, version);
                } finally {
                    autoUpdateInFlight = NULL;
                }
            })();

            return autoUpdateInFlight;
        };

        // Manual fallback behind the "Reload now" button shown when activation stalls.
        Updater.manualReload = () => triggerReload('manual', pendingVersion);

        // Nudge the service worker to re-check (used by the build-version poll). Any newly
        // installed worker surfaces via the updatefound/statechange path and notifies .NET.
        Updater.checkForUpdate = async () => {
            if (currentRegistration === NULL) {
                return;
            }
            try {
                await currentRegistration.update();
                const latest = await getLatestRegistration();
                syncRegistrationState(latest, false);
            } catch (err) {
                console.warn('[PWA Updater] checkForUpdate failed:', err);
            }
        };

        // Returns the version we navigated to apply (or null) and clears the flag. The caller
        // confirms it against the now-running version before announcing success, so a reload that
        // landed on an error page or looped does not produce a stale toast.
        Updater.consumeUpdateSuccess = () => {
            const version = readSession(successKey);
            removeSession(successKey);
            return version || NULL;
        };

        // Called by .NET once it has confirmed the running build matches what we updated to.
        // Resets the loop backstop so future legitimate deploys are not throttled.
        Updater.confirmUpToDate = () => {
            removeSession(reloadCountKey);
        };

        // User dismissed the stalled banner — disarm so nothing reloads behind them later.
        Updater.cancelAutoUpdate = () => {
            autoUpdateCancelled = true;
            stopActivationWatch();
            activationRequested = false;
            if (!reloading) {
                setStatus(Status.Idle, NULL);
            }
        };

        const requestUpdateCheck = (force = false) => {
            if (!force && Date.now() - lastUpdateCheckAt < updateCheckDebounceMs) {
                return;
            }
            lastUpdateCheckAt = Date.now();
            void Updater.checkForUpdate();
        };

        const handleRegistration = (registration, suppress) => {
            initialInstallation = registration.active === NULL;
            currentRegistration = registration;

            syncRegistrationState(registration, suppress);

            registration.addEventListener('updatefound', () => {
                console.log('[PWA Updater] update found - new service worker installing');
                syncRegistrationState(registration, false);
            });
        };

        // Register the service worker (skip bots) -----------------------------------
        if (!noRegister && !new RegExp(detectBotPattern, "i").test(navigator.userAgent)) {

            // Most reliable activation signal: the new worker has claimed the page.
            navigator.serviceWorker?.addEventListener('controllerchange', () => {
                if (activationRequested && !reloading) {
                    triggerReload('controllerchange', pendingVersion);
                }
            });

            // If the previous load navigated to apply an update, a waiting worker briefly lingering
            // on this load is the one we just activated — suppress re-detecting it as "new".
            const justUpdated = readSession(successKey) !== NULL;

            navigator.serviceWorker?.register(serviceWorkerScriptPath, {updateViaCache: 'none'})
                .then(reg => {
                    handleRegistration(reg, justUpdated);
                    requestUpdateCheck(true);
                })
                .catch(err => console.error('[PWA Updater] Registration failed:', err));

            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    requestUpdateCheck();
                }
            });
            window.addEventListener('pageshow', () => requestUpdateCheck());
            window.addEventListener('online', () => requestUpdateCheck(true));
        }
    })(UP.Updater ??= {});
})(window.UP ??= {});
